import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { emailNotificationService } from '../../../services/serviceZoneService';

const CompletionModal = ({ isOpen, onClose, appointment, onStatusUpdate }) => {
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const paymentOptions = [
    { value: 'card', label: 'Carte bancaire' },
    { value: 'cash', label: 'Espèces' },
    { value: 'check', label: 'Chèque' },
    { value: 'transfer', label: 'Virement' },
    { value: 'invoice', label: 'Facture à envoyer' }
  ];

  const handleComplete = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onStatusUpdate(appointment?.id, 'completed', {
        completionNotes: formData?.notes,
        actualDuration: formData?.duration,
        finalPrice: formData?.finalPrice,
        workPerformed: formData?.workPerformed,
        partsUsed: formData?.partsUsed,
        recommendations: formData?.recommendations
      });

      try {
        await emailNotificationService?.sendBookingNotification(
          {
            reference: appointment?.reference,
            customerName: appointment?.customerName,
            customerEmail: appointment?.customerEmail,
            serviceType: appointment?.serviceType,
            scheduledDate: appointment?.scheduledDate
          },
          'service_completed'
        );
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      onClose();
    } catch (error) {
      console.error('Completion error:', error);
      alert('Erreur lors de la finalisation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1020] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg shadow-lg max-w-md w-full animate-scale-in">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-success" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Terminer l'intervention</h3>
                <p className="text-sm text-muted-foreground">{appointment?.customerName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Service</span>
              <span className="text-sm font-medium text-foreground">{appointment?.serviceType}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Durée</span>
              <span className="text-sm font-medium text-foreground">{appointment?.estimatedDuration}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Début</span>
              <span className="text-sm font-medium text-foreground">
                {new Date(appointment?.startTime || Date.now())?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>

          <Input
            label="Montant de la facture"
            type="number"
            placeholder="0.00"
            value={invoiceAmount}
            onChange={(e) => setInvoiceAmount(e?.target?.value)}
            required
            description="Montant TTC en euros"
          />

          <Select
            label="Mode de paiement"
            options={paymentOptions}
            value={paymentMethod}
            onChange={setPaymentMethod}
            placeholder="Sélectionnez un mode de paiement"
            required
          />

          <Input
            label="Notes complémentaires"
            type="text"
            placeholder="Informations supplémentaires..."
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e?.target?.value)}
          />

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div className="text-xs text-primary">
                <p className="font-medium mb-1">Documents automatiques</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Facture envoyée par email au client</li>
                  <li>Certificat de garantie 6 mois</li>
                  <li>Rapport d'intervention avec photos</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button
            variant="default"
            iconName="CheckCircle"
            onClick={handleComplete}
            disabled={!invoiceAmount || !paymentMethod}
            className="flex-1"
          >
            Confirmer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompletionModal;